import { io, Socket } from "socket.io-client";
import { getSyncZConfig } from '../config/synczConfig';

// Unified internal backend: Always route directly to local origin API routes
const getBaseUrl = (): string => "";

class SyncZBaseClient {
  private token: string | null = null;
  private socket: Socket | null = null;
  private appId: string = "default";
  public endpoint: string = "";

  constructor() {
    this.token = null;
    this.appId = getSyncZConfig().appId || 'default';
    this.initSocket();
  }

  setEndpoint(_endpoint: string) {
    // Keep relative routes to ensure local Express / Vite backend is always used
    this.endpoint = "";
  }

  getEndpoint(): string {
    return "";
  }

  setToken(token: string) {
    this.token = token;
    if (typeof document !== 'undefined') {
      try {
        document.cookie = `syncz_session_token=${encodeURIComponent(token)}; path=/; max-age=2592000; SameSite=Lax`;
      } catch (e) {}
    }
    this.initSocket();
  }
  
  setAppId(appId: string) {
    this.appId = appId;
  }

  clearToken() {
    this.token = null;
    if (typeof document !== 'undefined') {
      try {
        document.cookie = "syncz_session_token=; path=/; max-age=0; SameSite=Lax";
      } catch (e) {}
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  getToken() {
    if (this.token) return this.token;
    if (typeof document !== 'undefined') {
      try {
        const match = document.cookie.match(/(?:^|;\s*)syncz_session_token=([^;]+)/);
        if (match) {
          this.token = decodeURIComponent(match[1]);
          return this.token;
        }
      } catch (e) {}
    }
    return null;
  }

  private initSocket() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    // Only configure socket when a valid server URL is present
    const baseUrl = this.getEndpoint();
    if (this.token && baseUrl && (baseUrl.startsWith('http://') || baseUrl.startsWith('https://'))) {
      try {
        this.socket = io(baseUrl, {
          auth: { token: this.token },
          query: { appId: this.appId },
          autoConnect: false,
          reconnection: false,
          transports: ['websocket']
        });
        
        this.socket.on("connect_error", () => {
          if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
          }
        });
      } catch (err) {
        this.socket = null;
      }
    }
  }

  getSocket() {
    return this.socket;
  }

  // --- REST API ---
  async apiCall(endpoint: string, method: string = "GET", body?: any, headers: Record<string, string> = {}): Promise<any> {
    const defaultHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      ...headers
    };

    if (this.token) {
      defaultHeaders["Authorization"] = `Bearer ${this.token}`;
      defaultHeaders["x-syncz-token"] = this.token;
    }
    
    if (this.appId && this.appId !== "default") {
      defaultHeaders["x-app-id"] = this.appId;
    }
    
    let finalEndpoint = endpoint;
    const isFunctionCall = endpoint.startsWith("/api/functions/") && !endpoint.endsWith("/execute");
    if (isFunctionCall) {
        finalEndpoint = `${endpoint}/execute`;
    }

    // Always route internally via relative path to local backend
    const fetchUrl = finalEndpoint.startsWith('/') ? finalEndpoint : `/${finalEndpoint}`;

    let res: Response;
    try {
        res = await fetch(fetchUrl, {
            method,
            headers: defaultHeaders,
            body: body ? JSON.stringify(body) : undefined
        });
    } catch (e: any) {
        throw new Error(e.message || "Network error connecting to auth server");
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
        throw new Error(data.error || data.message || `API Request Failed with status ${res.status}`);
    }
    return data;
  }

  // --- Database ---
  async query(sql: string, params: any[] = []): Promise<any[]> {
    let finalSql = sql;
    let finalParams = params;
    
    // Auto-inject ID for schema-less INSERTs
    const match = sql.match(/INSERT\s+INTO\s+([a-zA-Z_]+)\s*\(\s*data\s*\)\s*VALUES\s*\(\s*\$1\s*\)(.*)/i);
    if (match) {
        finalSql = `INSERT INTO ${match[1]} (id, data) VALUES ($1, $2)${match[2]}`;
        const id = `${match[1]}_${Date.now()}_${Math.random().toString(36).substring(2,9)}`;
        let payload: any = {};
        try {
            payload = JSON.parse(params[0] || "{}");
        } catch (e) {}
        payload.id = id;
        finalParams = [id, JSON.stringify(payload)];
    }

    try {
        const data = await this.apiCall('/api/postgres/query', 'POST', { sql: finalSql, params: finalParams || [] });
        return data?.rows || (Array.isArray(data) ? data : []);
    } catch (e: any) {
        return [];
    }
  }

  // --- Storage ---
  async uploadFile(file: File, fileAppId: string = "default") {
    const targetAppId = fileAppId === "default" ? this.appId : fileAppId;
    const formData = new FormData();
    formData.append("file", file);

    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
      headers["x-syncz-token"] = this.token;
    }
    if (targetAppId && targetAppId !== "default") {
      headers["x-app-id"] = targetAppId;
    }

    const uploadUrl = `/api/storage/${targetAppId}/upload`;

    let res: Response | null = null;
    try {
        res = await fetch(uploadUrl, { method: "POST", headers, body: formData });
    } catch (e) {
        throw new Error(`Failed to upload file ${file.name}`);
    }

    if (!res || !res.ok) {
        const errData = res ? await res.json().catch(() => ({})) : {};
        throw new Error(errData.error || errData.message || `Failed to upload file ${file.name}`);
    }

    const data = await res.json();
    return {
        ...data,
        url: data.url || this.getFileUrl(data.id, targetAppId)
    };
  }

  getFileUrl(fileId: string, fileAppId: string = "default") {
    const targetAppId = fileAppId === "default" ? this.appId : fileAppId;
    const tokenQuery = this.token ? `?token=${encodeURIComponent(this.token)}` : '';
    return `/api/storage/${targetAppId}/${fileId}${tokenQuery}`;
  }

  async listStorageFiles(fileAppId: string = "default") {
    const targetAppId = fileAppId === "default" ? this.appId : fileAppId;
    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const fetchUrl = `/api/storage/${targetAppId}`;
    const res = await fetch(fetchUrl, { method: "GET", headers }).catch(() => null);
    if (!res || !res.ok) return [];
    return await res.json();
  }

  async deleteStorageFile(fileId: string, fileAppId: string = "default") {
    const targetAppId = fileAppId === "default" ? this.appId : fileAppId;
    const headers: Record<string, string> = {};
    if (this.token) {
      headers["Authorization"] = `Bearer ${this.token}`;
    }

    const fetchUrl = `/api/storage/${targetAppId}/${fileId}`;
    const res = await fetch(fetchUrl, { method: "DELETE", headers }).catch(() => null);
    if (!res || !res.ok) return { success: false };
    return await res.json();
  }
}

export const synczbase = new SyncZBaseClient();
